// src/app/modules/Blacklist/pages/Blacklist.jsx
import React, { useEffect, useState, useRef } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import * as actions from "../../Dashbord/_redux/dashboard/Actions";
import { BlacklistTable } from "../database";
import { AddToBlacklistModal } from "../components/AddToBlacklistModal";
import { ConfirmActionModal } from '../../../components/ConfirmActionModal';
import {
  Ban, ShieldAlert, AlertTriangle, Filter,
  Search, Plus, XCircle, Globe,
} from 'lucide-react';
import './blacklist.scss';

export function Blacklist() {
  const dispatch = useDispatch();
  const isMountedRef = useRef(true);

  const { org, blacklist, blacklistMeta, networkStats, loading } = useSelector(
    (state) => ({
      org:          state.auth.organization,
      blacklist:    state.dashboard.blacklist,
      blacklistMeta: state.dashboard.blacklistMeta,
      networkStats: state.dashboard.networkStats,
      loading:      state.dashboard.listLoading,
    }),
    shallowEqual
  );

  const [currentPage,     setCurrentPage]     = useState(1);
  const [perPage]                             = useState(10);
  const [statusFilter,    setStatusFilter]    = useState('active');
  const [severityFilter,  setSeverityFilter]  = useState('');
  const [reasonFilter,    setReasonFilter]    = useState('');

  const [showAddModal,    setShowAddModal]    = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedEntry,   setSelectedEntry]   = useState(null);
  const [actionLoading,   setActionLoading]   = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (org?.organization_id) {
      fetchBlacklist();
      fetchNetworkStats();
    }
  }, [currentPage, statusFilter, severityFilter, reasonFilter]);

  function fetchBlacklist() {
    if (!isMountedRef.current) return;
    const params = { page: currentPage, limit: perPage, organization_id: org.organization_id };
    if (statusFilter)   params.status   = statusFilter;
    if (severityFilter) params.severity = severityFilter;
    if (reasonFilter)   params.reason   = reasonFilter;
    dispatch(actions.fetchAllData('blacklist', org.organization_id, params));
  }

  function fetchNetworkStats() {
    if (!isMountedRef.current) return;
    dispatch(actions.fetchAllData('blacklist_stats', org.organization_id));
  }

  function handleRemove(entry) { setSelectedEntry(entry); setShowRemoveModal(true); }

  async function handleRemoveConfirm() {
    setActionLoading(true);
    try {
      await dispatch(actions.addData('remove_from_blacklist', {
        blacklist_id:    selectedEntry.blacklist_id,
        organization_id: org.organization_id,
      }));
      fetchBlacklist();
      fetchNetworkStats();
      setShowRemoveModal(false);
    } catch (e) { console.error(e); }
    finally { setActionLoading(false); }
  }

  const stats = [
    {
      icon: <Ban size={16} />, bg: '#FEF2F2', color: '#DC2626',
      label: 'Total Blacklisted',
      value: blacklistMeta?.totalResults || 0,
    },
    {
      icon: <AlertTriangle size={16} />, bg: '#FFFBEB', color: '#D97706',
      label: 'Active Bans',
      value: blacklist?.filter(b => b.status === 'active').length || 0,
    },
    {
      icon: <Globe size={16} />, bg: '#EFF6FF', color: '#2563EB',
      label: 'Network Shared',
      value: networkStats?.network_shared || 0,
    },
    {
      icon: <ShieldAlert size={16} />, bg: '#F0EEFF', color: '#5341C4',
      label: 'Critical Threats',
      value: blacklist?.filter(b => b.severity === 'critical').length || 0,
    },
  ];

  return (
    <>
      <div className="blacklist-page">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="ac-header">
          <div className="ac-header__left">
            <h1>Blacklist</h1>
            <p>Banned visitors &amp; threat database</p>
          </div>
          <div className="ac-header__actions">
            <button className="ac-btn ac-btn--primary" onClick={() => setShowAddModal(true)}>
              <Plus size={13} />Add to Blacklist
            </button>
          </div>
        </div>

        {/* ── Stats strip ─────────────────────────────────────────────────── */}
        <div className="ac-card">
          <div className="ac-code-stats" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            {stats.map((s, i) => (
              <div key={i} className="ac-code-stat">
                <div className="ac-code-stat__icon" style={{ background: s.bg, color: s.color }}>
                  {s.icon}
                </div>
                <div>
                  <div className="ac-code-stat__value">{s.value}</div>
                  <div className="ac-code-stat__label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Filters ─────────────────────────────────────────────────────── */}
        <div className="ac-card">
          <div className="ac-card__body" style={{ padding: '11px 16px' }}>
            <div className="ac-filters">

              <div className="ac-search">
                <Search size={14} />
                <input placeholder="Search by name, phone, reason…" />
              </div>

              <select
                className="ac-select"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="appealed">Appealed</option>
                <option value="expired">Expired</option>
                <option value="removed">Removed</option>
              </select>

              <select
                className="ac-select"
                value={severityFilter}
                onChange={(e) => { setSeverityFilter(e.target.value); setCurrentPage(1); }}
              >
                <option value="">All Severities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>

              <select
                className="ac-select"
                value={reasonFilter}
                onChange={(e) => { setReasonFilter(e.target.value); setCurrentPage(1); }}
              >
                <option value="">All Reasons</option>
                <option value="theft">Theft</option>
                <option value="fraud">Fraud</option>
                <option value="assault">Assault</option>
                <option value="vandalism">Vandalism</option>
                <option value="harassment">Harassment</option>
                <option value="trespassing">Trespassing</option>
                <option value="suspicious_activity">Suspicious Activity</option>
              </select>

              <button
                className="ac-btn ac-btn--secondary"
                onClick={() => { setStatusFilter('active'); setSeverityFilter(''); setReasonFilter(''); }}
              >
                <XCircle size={13} />Reset
              </button>

              <button
                className="ac-apply-btn"
                onClick={() => { setCurrentPage(1); fetchBlacklist(); }}
              >
                <Filter size={13} />Apply
              </button>

            </div>
          </div>
        </div>

        {/* ── Table ───────────────────────────────────────────────────────── */}
        <div className="ac-card">
          <div className="ac-card__header">
            <div className="ac-card__title">
              <Ban size={14} />Blacklist Entries
            </div>
            <span className="ac-card__meta">
              {blacklistMeta?.totalResults || 0} total entries
            </span>
          </div>
          <div className="ac-table-wrap">
            <BlacklistTable
              blacklist={blacklist || []}
              loading={loading}
              onRemove={handleRemove}
              currentPage={currentPage}
              perPage={perPage}
              totalCount={blacklistMeta?.totalResults || 0}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>

      </div>

      <AddToBlacklistModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        organizationId={org?.organization_id}
        onSuccess={() => { setCurrentPage(1); fetchBlacklist(); fetchNetworkStats(); }}
      />

      <ConfirmActionModal
        show={showRemoveModal}
        onHide={() => setShowRemoveModal(false)}
        onConfirm={handleRemoveConfirm}
        title="Remove from Blacklist"
        message={
          <div>
            <p>Remove <strong>{selectedEntry?.visitor_name}</strong> from blacklist?</p>
            <p className="mt-2 text-muted">They will be able to access the premises again.</p>
          </div>
        }
        confirmText="Remove"
        type="danger"
        loading={actionLoading}
      />
    </>
  );
}